import React, { useState } from 'react';
import { Link, graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import Image from '@/components/elements/image';
import AllContentIcon from '@/images/all-content-icon.svg';
import LibraryVerticalScrollbar from '@/components/library-elements/library-vertical-scrollbar';
import { LibraryPages, getLibraryUrlPrefix } from '@/utils/url';
import ThumborImageService from '@/utils/thumbor-image-service';

export default function LibraryCategoriesNavigation({
	locale,
	isAllContent = false,
	currentSlug = '',
	categories = [],
	subCategories = [],
	typeFilterOnChange = null,
}) {
	const { t } = useTranslation();
	const thumborServiceInstance = ThumborImageService.getInstance();

	const allCategories = [
		{
			id: 'ALL_CONTENT',
			slug: t('gynzy-library-url-structure.items'),
			subCategories: [],
			model: {
				name: t('gynzy-library.all-content'),
			},
			isActive: isAllContent,
		},
		...categories
			.filter(c => c.hasLibraryItems === true)
			.map(c => ({
				...c,
				isActive: !isAllContent && currentSlug.split('/').includes(c.slug),
			})),
	];

	const [toolsChecked, setToolsCheckState] = useState(true);
	const [lessonsChecked, setLessonsCheckState] = useState(true);

	const handleToolsFilterChange = checked => {
		setToolsCheckState(checked);
		typeFilterOnChange?.({ showTools: checked, showLessons: lessonsChecked });
	};

	const handleLessonsFilterChange = checked => {
		setLessonsCheckState(checked);
		typeFilterOnChange?.({ showTools: toolsChecked, showLessons: checked });
	};

	const isActiveIndex = allCategories.findIndex(c => c.isActive);
	return (
		<>
			<div className="container flex py-1">
				{typeFilterOnChange && (
					<div className="m-1">
						<div className="prose rounded-xl w-32 h-28 border border-heather text-xs flex flex-col justify-center items-center">
							<div>
								<div className="pb-2 font-semibold">{t('gynzy-library.show-content')}</div>
								<div className="pb-1">
									<label htmlFor="tools-checkbox" className="flex align-center">
										<input
											id="tools-checkbox"
											type="checkbox"
											checked={toolsChecked}
											className="mr-2"
											onChange={() => handleToolsFilterChange(!toolsChecked)}
										/>
										{t('gynzy-library.tools')}
									</label>
								</div>
								<div>
									<label htmlFor="lessons-checkbox" className="flex align-center">
										<input
											id="lessons-checkbox"
											type="checkbox"
											checked={lessonsChecked}
											className="mr-2"
											onChange={() => handleLessonsFilterChange(!lessonsChecked)}
										/>
										{t('gynzy-library.lessons')}
									</label>
								</div>
							</div>
						</div>
					</div>
				)}
				<div className={typeFilterOnChange ? 'w-[calc(100%_-_8.5rem)]' : 'w-full'}>
					<LibraryVerticalScrollbar initialScrollIndex={isActiveIndex} centerItems={true}>
						{allCategories.map(category => {
							const path = `/${getLibraryUrlPrefix(locale, t, LibraryPages.CATEGORY)}/${
								category.slug
							}`.toLocaleLowerCase();

							return (
								<div key={category.id}>
									<div className={`rounded-xl hover:bg-sky-lightest m-1 ${category.isActive ? 'bg-sky-lightest' : ''}`}>
										<Link to={path} className="text-base font-sans text-midnight">
											<div className="w-32 h-28 text-center break-words flex flex-col justify-center text-xs">
												<div className="flex justify-center">
													{category.model.icon && (
														<Image
															appearance="h-14 m-0 pb-2"
															src={thumborServiceInstance.getThumborUrlForImage(category.model.icon, 112, 112)}
															alt={category.model.name}
														/>
													)}
													{category.id === 'ALL_CONTENT' && <AllContentIcon className="w-14 h-14 m-0 pb-2" />}
												</div>
												{category.model.name}
											</div>
										</Link>
									</div>
								</div>
							);
						})}
					</LibraryVerticalScrollbar>
				</div>
			</div>
			{subCategories.length > 0 && (
				<div className="container flex py-1">
					<LibraryVerticalScrollbar>
						{subCategories.map(category => {
							const path = `/${getLibraryUrlPrefix(locale, t, LibraryPages.CATEGORY)}/${
								category.slug
							}`.toLocaleLowerCase();
							return (
								<Link to={path} className="whitespace-nowrap">
									<div
										key={category.id}
										className="border border-heather rounded my-1.5 mr-4 px-4 py-1.5 text-xs text-shuttle-grey hover:text-mariner active:bg-aqua-haze"
									>
										{category.model.name}
									</div>
								</Link>
							);
						})}
					</LibraryVerticalScrollbar>
				</div>
			)}
		</>
	);
}

export const query = graphql`
	# Re-usable query that can be used within multiple page-templates
	fragment NavigationCategoriesQuery on Query {
		navigationCategories: allLibraryCategory(
			filter: {
				model: { parent_id: { eq: null }, language_code: { eq: $languageCode }, country_code: { eq: $countryCode } }
			}
			sort: { model: { sort: ASC } }
		) {
			edges {
				node {
					id
					slug
					hasLibraryItems
					model {
						id
						icon
						name
					}
				}
			}
		}
	}
`;
